import { Check, Clock, X as XIcon } from 'lucide-react';
import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/db';
import { captureSilentError } from '../../services/posthog';
import { useUIStore } from '../../stores/useUIStore';
import { FriendRequest, User } from '../../types';

interface FriendRequestsProps {
  outgoingRequests: { request: FriendRequest, toUser: User }[];
  incomingRequests: { request: FriendRequest, fromUser: User }[];
  onRefresh: () => Promise<void>;
}

export const FriendRequests: React.FC<FriendRequestsProps> = ({
  outgoingRequests,
  incomingRequests,
  onRefresh
}) => {
  const { refreshUser } = useAuth();

  const handleRespond = async (reqId: string, accept: boolean) => {
    try {
      await db.respondToRequest(reqId, accept);
      
      if (accept && refreshUser) {
          await refreshUser();
      }
      
      await onRefresh(); // Refresh requests list
    } catch (e) {
      captureSilentError("FriendRequests.handleRespond", e);
      useUIStore.getState().showToast("Failed to respond: " + (e as Error).message, 'error');
    }
  };

  return (
    <>
        {/* Pending Requests Sent */}
        {outgoingRequests.length > 0 && (
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">Requests You Sent</h4>
                <div className="space-y-2">
                    {outgoingRequests.map(({ request, toUser }, index) => (
                        <div 
                            key={request.id} 
                            className="flex items-center justify-between p-3 bg-white border border-brand-cream-dark rounded-xl shadow-sm animate-slide-in"
                            style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
                        >
                            <div className="flex items-center gap-3">
                                <img src={toUser.photoURL} className="w-10 h-10 rounded-full bg-brand-cream-dark object-cover" />
                                <div>
                                    <p className="font-bold text-sm text-brand-dark">{toUser.displayName}</p>
                                    <p className="text-xs text-brand-dark-light">@{toUser.username}</p>
                                </div>
                            </div>
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg text-xs font-bold flex gap-1 items-center">
                                <Clock size={12}/> Pending
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* Incoming Requests */}
        {incomingRequests.length > 0 && (
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">Requests To Respond</h4>
                <div className="space-y-2">
                    {incomingRequests.map(({request, fromUser}, index) => (
                        <div 
                            key={request.id} 
                            className="flex items-center justify-between p-3 bg-white border border-brand-cream-dark rounded-xl shadow-sm animate-slide-in"
                            style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
                        >
                            <div className="flex items-center gap-3">
                                <img src={fromUser.photoURL} className="w-10 h-10 rounded-full bg-white object-cover" />
                                <div>
                                    <p className="font-bold text-sm text-brand-dark">{fromUser.displayName}</p>
                                    <p className="text-xs text-brand-dark-light">wants to add you</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => handleRespond(request.id, false)} className="p-2 bg-brand-cream-dark text-brand-red rounded-lg hover:bg-red-100"><XIcon size={16}/></button>
                                <button onClick={() => handleRespond(request.id, true)} className="p-2 bg-brand-red text-white rounded-lg hover:bg-brand-red-hover"><Check size={16}/></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </>
  );
};
